import React from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import Footer from '../Footer/Footer';
import { Helmet } from 'react-helmet-async';

import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/opacity.css';

import buildicon from '../../assets/img/build-icon.png';
import { routes } from '../../routing/routes';

export default function Roadmap() {
  return (
    <m.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="media"
    >
      <Helmet>
        <title>Roadmap - DuskBreakers</title>
        <meta
          name="description"
          content="We've been busy! Learn more about what the DuskBreakers team has planned for the future."
        />
      </Helmet>

      <section>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <LazyLoadImage
                className="img-fluid"
                alt="Duskbreakers building together"
                src={buildicon}
                effect="opacity"
              />

              <h1>Roadmap</h1>

              <p>
                DuskBreakers is an exciting sci-fi saga in an alternate universe, unfolding across
                NFTs, comics, animations, and video games. We are building this fun, immersive world
                in collaboration with our community.
              </p>

              <p className="mb-4">
                The most recent update to our Roadmap was completed on August 19, 2022. For more
                information on these changes, read our{' '}
                <a
                  href=" https://medium.com/metatheoryinc/from-breakers-to-gribbles-and-beyond-195895781e37"
                  target="_new"
                >
                  From Breakers to Gribbles and Beyond
                </a>{' '}
                update blog.
              </p>

              <h2>Completed</h2>
              <h3>2021</h3>
              <p>
                <strong>Play-to-Mint via “The Big Break” Event (December)</strong> - We launched our
                first mini-game called “The Recruit Simulator,” where players learned to “Break the
                Dusk.” Prospective buyers played the game to earn a chance to mint a Genesis
                DuskBreakers NFT.
              </p>
              <p>
                <strong>Genesis DuskBreakers Mint (December)</strong> - We released our genesis
                collection on December 6.
              </p>

              <h3>2022</h3>
              <p>
                <strong>Beast Battler Mini-Game and Competition (January)</strong> - We launched a
                second mini-game with a multi-week contest with prizes for both winners and
                participants.
              </p>
              <p>
                <strong>Games Launcher (March)</strong> - We released our{' '}
                <Link to={routes.launcher()}>Games Launcher</Link>, which allows installation of our
                games (via executable) on Windows PCs. The Launcher includes all of our existing
                titles while paving the way for future games! The Launcher provides a secure and
                user-friendly way for our games to interact with the Web3 [crypto] ecosystem.
              </p>
              <p>
                <strong>Listed on IGDB (March)</strong> - We listed “DuskBreakers” in the Internet
                Games Database (IGDB), which Twitch uses to populate their Games directory, allowing
                creators to find and select the game.
              </p>
              <p>
                <strong>First Animated Video (March)</strong> - Showcasing our 3D video
                capabilities, we produced a 3D cinematic featuring the Blue Corrupted, a 1/1 NFT
                from our genesis collection.
              </p>
              <p>
                <strong>Lore Update (March)</strong> - The first entries into the lore/world section
                are released. Introducing the historical timeline, the Dusk spacecraft, the city of
                Machafuko, community owned Breakers, and more!
              </p>
              <p>
                <strong>Second Corrupted Video: Beast Battler Champions (April)</strong> - We
                produced a second 3D cinematic featuring the Blue Corrupted, honoring some of the
                top DuskBreakers from our Beast Battler Competition.
              </p>

              <p>
                <strong>Webcomic: Joli&apos;s Journey (May)</strong> - An original, multi-chapter
                webcomic series set in the DuskBreakers universe. The story follows Joli, a new
                DuskBreaker recruit, on her first mission into the Dusk, and includes the addition
                of canonized, community-owned Breakers as key characters.
              </p>

              <p>
                <strong>First Breaker Holder Meetup at NFT.NYC (June)</strong> - Our first Breaker
                Holder Meetup took place in New York, where numerous staff and Holders were in town
                for NFT.NYC 2022. Over 40 people in attendance!
              </p>

              <p>
                <strong>“PayDay” Cinematic (June)</strong> - A new 3D cinematic featuring the reveal
                of a gribble egg, a teaser alluding to The Gribbles NFT collection.
              </p>

              <p>
                <strong>Twilight Shift (August)</strong> - <i>Twilight Shift</i> is a roguelike
                dungeon crawler video game in which you defy your corporate overlords to quest for
                precious materials while avoiding the alien denizens of a derelict spaceship. An
                exclusive playtest for Genesis DuskBreakers Holders was first launched in July 2022.
              </p>

              <p>
                <strong>Gribble Egg Mint (August)</strong> - Launch of The Gribbles NFT collection
                with 20,000 Original Gribbles (OGs).This represents the second collection of
                playable in-game assets and access passes to our broader DuskBreakers universe and
                community experiences.
              </p>

              <p>
                <strong>The Gribble Eggsperience (August)</strong> - An interactive web experience
                for Gribble Egg NFT owners to interact with their eggs, leveling them up and
                maximizing egg evolution by obtaining and applying experience points, dubbed
                OmniGrow.
              </p>

              <p>
                <strong>Adrian&apos;s Sanctuary (August)</strong> - An original visual novel
                experience featuring Adrian the Exobiologist, your guide to hatching and evolving
                gribble eggs. Join him in his grand experiment, and learn more about his shadowy
                past by visiting and interacting with <i>Adrian&apos;s Sanctuary.</i>
              </p>

              <p className="mb-5">
                <strong>DuskBreakers NFT License Terms (August)</strong> - DuskBreakers franchise
                license terms covering IP ownership and the usage of NFT art in creative
                collaboration with the community.
              </p>

              <h2>In Progress</h2>
              <p>Projects in development over the next 12 months:</p>
              <ul className="mb-5">
                <li>Gribble NFTs: fully-rigged 3D models</li>
                <li>Breaker + Gribble Interactive Environment</li>
                <li>Collection of Gribble Mini-Games and Multiplayer Competitive Games</li>
                <li>Gribble Breeding</li>
                <li>Upgrades to Twilight Shift PVE</li>
                <li>Twilight Shift PVP</li>
                <li>Breaker 3D Models</li>
                <li>DuskBreakers Universal Account System</li>
              </ul>

              <h2>Future Thoughts</h2>
              <ul>
                <li>More Events</li>
                <li>Merch &amp; Swag</li>
                <li>More Holder Benefits!</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </m.main>
  );
}
